import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Download, Users, Mail, Building2, Globe, LogOut, 
  RefreshCw, Plus, Edit, Trash2, MessageCircle, Briefcase,
  Save, Filter, Upload, FileSpreadsheet, Handshake, Bell, CheckCircle2, XCircle, Clock, Eye, ArrowRight, X, Camera, Link2, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { 
  getAdminLeads, getAdminStats, getNewsletterSubscribers, 
  downloadLeadsCSV, adminLogout, getCurrentUser, togglePropertyVisibility,
  getAllCountries, getAdminCountries, createCountry,
  getAdminPartners, createPartner, updatePartner, deletePartner, uploadBrochure,
  getAdminNotifications, getNotificationCount, markNotificationRead, markAllNotificationsRead,
  approveNotification, rejectNotification
} from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';
import Logo from '../components/Logo';
import FeatureSelector from '../components/FeatureSelector';
import PropertyFormWizard from '../components/PropertyFormWizard';
import ImageUploadBox from '../components/ImageUploadBox';
import AdminTable from '../components/AdminTable';

const API_BASE_URL = '';

// Download helper
function downloadFile(url, token) {
  fetch(url, {
    credentials: 'include',
    headers: { 'Authorization': `Bearer ${token}` }
  })
  .then(r => r.blob())
  .then(blob => {
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    const disposition = url.split('/').pop();
    a.download = disposition;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(a.href);
    document.body.removeChild(a);
    toast.success('Download started');
  })
  .catch(() => toast.error('Download failed'));
}

// Export buttons component
function ExportButtons({ tab }) {
  const token = localStorage.getItem('adminToken');
  return (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white h-8 text-xs"
        onClick={() => downloadFile(`${API_BASE_URL}/api/admin/export/${tab}/csv`, token)}
        data-testid={`export-${tab}-csv`}>
        <Download className="h-3 w-3 mr-1" /> CSV
      </Button>
      <Button size="sm" variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white h-8 text-xs"
        onClick={() => downloadFile(`${API_BASE_URL}/api/admin/export/${tab}/xls`, token)}
        data-testid={`export-${tab}-xls`}>
        <FileSpreadsheet className="h-3 w-3 mr-1" /> XLS
      </Button>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [whatsappLeads, setWhatsappLeads] = useState([]);
  const [careerApplications, setCareerApplications] = useState([]);
  const [properties, setProperties] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [imageUploadProperty, setImageUploadProperty] = useState(null);
  const [showUrlImport, setShowUrlImport] = useState(false);
  const [urlImportData, setUrlImportData] = useState({ name: '', url: '' });
  const [urlImporting, setUrlImporting] = useState(false);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const csvInputRef = useRef(null);
  const [propertyFilters, setPropertyFilters] = useState({
    country: 'all', type: 'all', budget: 'all', config: 'all', partner: 'all',
  });
  const [propertyForm, setPropertyForm] = useState({
    title: '', description: '', price_usd: '', country: '', region: '', city: '',
    property_type: '', bedrooms: '', bathrooms: '', area_sqft: '', image: '', images: '',
    visa_eligible: true, visa_type: 'Golden Visa', min_investment: '', features: '', partner: '',
    latitude: '', longitude: '', video_url: ''
  });
  const [formStep, setFormStep] = useState(1);
  const [partnerForm, setPartnerForm] = useState({
    name: '', email: '', password: '', company: '', logo_url: '', description: '', country: '', city: '', website: '', status: 'Active', tier: 'Basic', credits: 0, partner_type: 'developer', billing_cycle: 'monthly', phone: '', designation: ''
  });
  const [adminCountries, setAdminCountries] = useState([]);
  const [dynamicCountries, setDynamicCountries] = useState([]);
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [countryForm, setCountryForm] = useState({
    name: '', region: '', visa_name: '', visa_type: '', min_investment: '',
    visa_benefits: '', visa_requirements: '', processing_time: '', validity: '',
    residency_writeup: '', image: ''
  });
  const [countryCreating, setCountryCreating] = useState(false);
  const [customCountryInput, setCustomCountryInput] = useState('');
  const [brochureUploading, setBrochureUploading] = useState(false);
  const brochureInputRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [actionRequired, setActionRequired] = useState(0);

  const regions = ['Europe', 'Middle East', 'Americas', 'Asia', 'Africa', 'Oceania', 'South America', 'Caribbean'];
  const propertyTypes = ['Villa', 'Apartment', 'Penthouse', 'Townhouse', 'Estate', 'House', 'Condo', 'Flat'];
  const visaTypes = ['Golden Visa', 'Investor Visa', 'EB-5 Investor Visa', 'Start-Up Visa', 'Global Investor Programme', 'Residency Visa'];

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
        fetchData();
      } catch {
        toast.error('Please login to access the dashboard');
        navigate('/admin');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const config = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

      const [leadsData, statsData, subscribersData, whatsappData, careersData, propertiesData, partnersData, countriesData, adminCountriesData] = await Promise.all([
        getAdminLeads(),
        getAdminStats(),
        getNewsletterSubscribers(),
        axios.get(`${API_BASE_URL}/api/admin/whatsapp-leads`, config).then(r => r.data).catch(() => []),
        axios.get(`${API_BASE_URL}/api/admin/career-applications`, config).then(r => r.data).catch(() => []),
        axios.get(`${API_BASE_URL}/api/admin/properties`, config).then(r => r.data).catch(() => ({ properties: [] })),
        getAdminPartners().catch(() => ({ partners: [] })),
        getAllCountries().catch(() => ({ countries: [] })),
        getAdminCountries().catch(() => ({ countries: [] }))
      ]);
      
      setLeads(leadsData);
      setStats(statsData);
      setSubscribers(subscribersData);
      setWhatsappLeads(whatsappData);
      setCareerApplications(careersData);
      setProperties(propertiesData.properties || []);
      setPartners(partnersData.partners || []);
      setDynamicCountries(countriesData.countries || []);
      setAdminCountries(adminCountriesData.countries || []);
      
      // Fetch notifications
      try {
        const notifData = await getAdminNotifications();
        setNotifications(notifData.notifications || []);
        setUnreadCount(notifData.unread_count || 0);
        setActionRequired(notifData.action_required || 0);
      } catch { /* ignore */ }
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      toast.success('Logged out successfully');
      navigate('/admin');
    } catch { /* ignore */ }
  };

  // ======== PROPERTY HANDLERS ========
  const resetPropertyForm = () => {
    setPropertyForm({ title: '', description: '', price_usd: '', country: '', region: '', city: '', property_type: '', bedrooms: '', bathrooms: '', area_sqft: '', image: '', images: '', visa_eligible: true, visa_type: 'Golden Visa', min_investment: '', features: '', partner: '', latitude: '', longitude: '', video_url: '' });
    setEditingProperty(null);
    setFormStep(1);
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const propertyData = {
      ...propertyForm,
      price_usd: parseFloat(propertyForm.price_usd),
      bedrooms: parseInt(propertyForm.bedrooms),
      bathrooms: parseInt(propertyForm.bathrooms),
      area_sqft: parseInt(propertyForm.area_sqft),
      min_investment: parseFloat(propertyForm.min_investment) || 250000,
      images: propertyForm.images ? propertyForm.images.split(',').map(s => s.trim()) : [propertyForm.image],
      features: propertyForm.features ? propertyForm.features.split(',').map(s => s.trim()) : [],
      developer: propertyForm.partner || '',
      partner: propertyForm.partner || '',
      latitude: propertyForm.latitude ? parseFloat(propertyForm.latitude) : null,
      longitude: propertyForm.longitude ? parseFloat(propertyForm.longitude) : null,
      video_url: propertyForm.video_url || ''
    };
    try {
      if (editingProperty) {
        await axios.put(`${API_BASE_URL}/api/admin/properties/${editingProperty}`, propertyData, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
        toast.success('Property updated');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/properties`, propertyData, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
        toast.success('Property created');
      }
      setIsPropertyModalOpen(false);
      resetPropertyForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setIsPropertyModalOpen(true);
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/properties/${propertyId}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success('Property deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    }
  };

  const handleToggleVisibility = async (propertyId) => {
    try {
      const result = await togglePropertyVisibility(propertyId);
      toast.success(`Property ${result.visible ? 'shown' : 'hidden'} on website`);
      fetchData();
    } catch {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleBulkDelete = async (collection, ids) => {
    if (!window.confirm(`Delete ${ids.length} item(s)? This cannot be undone.`)) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API_BASE_URL}/api/admin/bulk-delete`, { collection, ids }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${ids.length} item(s) deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handlePurgeAll = async () => {
    if (!window.confirm('PURGE ALL DATA? This will delete ALL properties, partners, leads, subscriptions, and notifications. Admin account will be preserved. This CANNOT be undone.')) return;
    if (!window.confirm('Are you ABSOLUTELY sure? Type "yes" mentally and click OK.')) return;
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/purge-all-data`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    }
  };



  // CSV Upload
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const text = await file.text();
      const token = localStorage.getItem('adminToken');
      const result = await axios.post(`${API_BASE_URL}/api/admin/properties/upload-csv`, text, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'text/plain' },
        withCredentials: true
      });
      const data = result.data;
      toast.success(`${data.created} properties imported`);
      if (data.errors?.length) toast.error(`${data.errors.length} rows had errors`);
      fetchData();
    } catch {
      toast.error('CSV upload failed');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const handleDownloadSampleCsv = () => {
    const token = localStorage.getItem('adminToken');
    downloadFile(`${API_BASE_URL}/api/admin/properties/sample-csv`, token);
  };

  // ======== PARTNER HANDLERS ========
  const resetPartnerForm = () => {
    setPartnerForm({ name: '', email: '', password: '', company: '', logo_url: '', description: '', country: '', city: '', website: '', status: 'Active', tier: 'Basic', credits: 0, partner_type: 'developer', billing_cycle: 'monthly', phone: '', designation: '' });
    setEditingPartner(null);
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        const updateData = { ...partnerForm };
        delete updateData.password; // Don't send password on update unless changed
        if (partnerForm.password) updateData.password = partnerForm.password;
        await updatePartner(editingPartner, updateData);
        toast.success('Partner updated');
      } else {
        await createPartner(partnerForm);
        toast.success('Partner created');
      }
      setIsPartnerModalOpen(false);
      resetPartnerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save partner');
    }
  };

  const handleEditPartner = (partner) => {
    setEditingPartner(partner.id);
    setPartnerForm({
      name: partner.name, email: partner.email || '', password: '',
      company: partner.company || '', logo_url: partner.logo_url || '',
      description: partner.description || '', country: partner.country || '',
      city: partner.city || '', website: partner.website || '',
      status: partner.status || 'Active', tier: partner.tier || 'Basic',
      credits: partner.credits || 0, partner_type: partner.partner_type || 'developer',
      billing_cycle: partner.billing_cycle || 'monthly',
      phone: partner.phone || '', designation: partner.designation || ''
    });
    setIsPartnerModalOpen(true);
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await deletePartner(partnerId);
      toast.success('Partner deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete partner');
    }
  };

  // ======== BROCHURE UPLOAD ========
  const handleBrochureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBrochureUploading(true);
    try {
      const result = await uploadBrochure(file);
      if (result.ai_extracted && Object.keys(result.ai_extracted).length > 0) {
        const ai = result.ai_extracted;
        setPropertyForm({
          title: ai.title || '', description: ai.description || '',
          price_usd: ai.price_usd?.toString() || '', country: ai.country || '',
          region: ai.region || '', city: ai.city || '',
          property_type: ai.property_type || '', bedrooms: ai.bedrooms?.toString() || '',
          bathrooms: ai.bathrooms?.toString() || '', area_sqft: ai.area_sqft?.toString() || '',
          image: '', images: '', visa_eligible: ai.visa_eligible ?? true,
          visa_type: ai.visa_type || 'Golden Visa',
          min_investment: ai.min_investment?.toString() || '',
          features: ai.features?.join(', ') || '', partner: '',
          latitude: ai.latitude?.toString() || '', longitude: ai.longitude?.toString() || '',
          video_url: ''
        });
        setFormStep(1);
        setIsPropertyModalOpen(true);
        toast.success('AI extracted property details from brochure! Please review and add images.');
      } else {
        toast.info('Brochure uploaded but AI could not extract details. Please fill manually.');
        setIsPropertyModalOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Brochure upload failed');
    } finally {
      setBrochureUploading(false);
      if (brochureInputRef.current) brochureInputRef.current.value = '';
    }
  };

  // ======== NOTIFICATION HANDLERS ========
  const handleApprove = async (notifId) => {
    try {
      await approveNotification(notifId);
      toast.success('Property approved and now live!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    }
  };

  const handleReject = async (notifId) => {
    if (!window.confirm('Reject this property? It will not appear on the website.')) return;
    try {
      await rejectNotification(notifId);
      toast.success('Property rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rejection failed');
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await markNotificationRead(notifId);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, status: 'read' } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch { /* ignore */ }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'new_property': return <Building2 className="h-4 w-4 text-blue-400" />;
      case 'property_edit': return <Edit className="h-4 w-4 text-yellow-400" />;
      case 'visibility_change': return <Eye className="h-4 w-4 text-purple-400" />;
      case 'new_partner': return <Handshake className="h-4 w-4 text-green-400" />;
      default: return <Bell className="h-4 w-4 text-[#D4AF37]" />;
    }
  };

  const getNotifColor = (type) => {
    switch (type) {
      case 'new_property': return 'border-l-blue-400';
      case 'property_edit': return 'border-l-yellow-400';
      case 'visibility_change': return 'border-l-purple-400';
      case 'new_partner': return 'border-l-green-400';
      default: return 'border-l-[#D4AF37]';
    }
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  // Derived filter options
  const filterOptions = useMemo(() => {
    const allCountries = [...new Set(properties.map(p => p.country).filter(Boolean))].sort();
    const allTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))].sort();
    const allPartners = [...new Set(properties.map(p => p.partner || p.developer).filter(Boolean))].sort();
    const allBedrooms = [...new Set(properties.map(p => p.bedrooms).filter(b => b && b > 0))].sort((a, b) => a - b);
    return { allCountries, allTypes, allPartners, allBedrooms };
  }, [properties]);

  const filteredProperties = useMemo(() => {
    return properties.filter(p => {
      if (propertyFilters.country !== 'all' && p.country !== propertyFilters.country) return false;
      if (propertyFilters.type !== 'all' && p.property_type !== propertyFilters.type) return false;
      if (propertyFilters.partner !== 'all' && (p.partner || p.developer || '') !== propertyFilters.partner) return false;
      if (propertyFilters.config !== 'all' && p.bedrooms !== parseInt(propertyFilters.config)) return false;
      if (propertyFilters.budget !== 'all') {
        const price = p.price_usd;
        switch (propertyFilters.budget) {
          case 'under500k': if (price >= 500000) return false; break;
          case '500k-1m': if (price < 500000 || price >= 1000000) return false; break;
          case '1m-5m': if (price < 1000000 || price >= 5000000) return false; break;
          case '5m-10m': if (price < 5000000 || price >= 10000000) return false; break;
          case 'above10m': if (price < 10000000) return false; break;
          default: break;
        }
      }
      return true;
    });
  }, [properties, propertyFilters]);

  // Partner names for autocomplete
  const partnerNames = useMemo(() => partners.map(d => d.company || d.name), [partners]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'notifications', label: 'Notifications', count: actionRequired, highlight: actionRequired > 0 },
    { id: 'leads', label: 'Leads', count: leads.length },
    { id: 'whatsapp', label: 'WhatsApp', count: whatsappLeads.length },
    { id: 'newsletter', label: 'Newsletter', count: subscribers.length },
    { id: 'careers', label: 'Applications', count: careerApplications.length },
    { id: 'properties', label: 'Properties', count: properties.length },
    { id: 'partners', label: 'Partners', count: partners.length },
    { id: 'countries', label: 'Countries', count: adminCountries.length },
  ];

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#1A1D24] border-b border-[#2A2F3A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Logo size="small" showText={false} />
              <div>
                <h1 className="text-white font-medium">
                  <span className="text-[#D4AF37]">Mulberry</span> <span className="text-white">World</span> Admin
                </h1>
                <p className="text-[#A1A1AA] text-xs">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setActiveTab('notifications')} 
                className="relative text-[#A1A1AA] hover:text-white transition-colors p-2" 
                data-testid="notification-bell"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#A1A1AA] hover:text-white" data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handlePurgeAll} className="text-red-400 hover:text-red-300" data-testid="purge-all-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Purge All Data
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#A1A1AA] hover:text-white" data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
            {[
              { label: 'Leads', value: stats.total_leads, icon: Users, color: '#D4AF37' },
              { label: 'WhatsApp', value: stats.whatsapp_leads || 0, icon: MessageCircle, color: '#25D366' },
              { label: 'Subscribers', value: stats.newsletter_subscribers, icon: Mail, color: '#D4AF37' },
              { label: 'Applications', value: stats.career_applications || 0, icon: Briefcase, color: '#D4AF37' },
              { label: 'Properties', value: stats.total_properties, icon: Building2, color: '#D4AF37' },
              { label: 'Partners', value: stats.partners || 0, icon: Handshake, color: '#D4AF37' },
              { label: 'Countries', value: stats.countries_covered, icon: Globe, color: '#D4AF37' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A1A1AA] text-xs">{s.label}</p>
                    <p className="text-2xl text-white font-semibold">{s.value}</p>
                  </div>
                  <s.icon className="h-5 w-5" style={{ color: s.color }} />
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center space-x-2 mb-6 border-b border-[#2A2F3A] overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-4 text-sm font-medium transition-colors whitespace-nowrap relative ${activeTab === tab.id ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#A1A1AA] hover:text-white'}`}
              data-testid={`${tab.id}-tab`}>
              {tab.id === 'notifications' ? (
                <span className="flex items-center gap-1.5">
                  <Bell className="h-3.5 w-3.5" />
                  {tab.label}
                  {tab.highlight && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                  {tab.count > 0 && <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 rounded-full">{tab.count}</span>}
                </span>
              ) : (
                <>{tab.label} ({tab.count})</>
              )}
            </button>
          ))}
        </div>

        {/* ======== NOTIFICATIONS TAB ======== */}
        {activeTab === 'notifications' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white" data-testid="notifications-heading">
                Notifications {unreadCount > 0 && <span className="text-sm text-red-400 ml-2">({unreadCount} unread)</span>}
              </h2>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button size="sm" variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white h-8 text-xs" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Mark All Read
                  </Button>
                )}
              </div>
            </div>

            {notifications.length === 0 ? (
              <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-12 text-center">
                <Bell className="h-12 w-12 text-[#2A2F3A] mx-auto mb-4" />
                <p className="text-[#A1A1AA] text-lg mb-1">No notifications yet</p>
                <p className="text-[#A1A1AA]/60 text-sm">Notifications will appear here when partners submit properties, make changes, or when new partners are added.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 border-l-4 ${getNotifColor(notif.type)} transition-all ${notif.status === 'unread' ? 'bg-[#1A1D24]' : 'bg-[#0B0C10]/50 opacity-80'}`}
                    onClick={() => notif.status === 'unread' && handleMarkRead(notif.id)}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-[#2A2F3A] flex items-center justify-center">
                        {getNotifIcon(notif.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white text-sm font-medium">{notif.title}</span>
                          {notif.status === 'unread' && <span className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />}
                          {notif.requires_action && !notif.action_taken && (
                            <Badge className="bg-red-500/10 text-red-400 border-0 text-[10px]">Action Required</Badge>
                          )}
                          {notif.action_taken === 'approved' && (
                            <Badge className="bg-green-500/10 text-green-400 border-0 text-[10px]">Approved</Badge>
                          )}
                          {notif.action_taken === 'rejected' && (
                            <Badge className="bg-red-500/10 text-red-400 border-0 text-[10px]">Rejected</Badge>
                          )}
                        </div>
                        <p className="text-[#A1A1AA] text-sm mb-2">{notif.message}</p>
                        
                        {/* Changes highlight */}
                        {notif.changes && Object.keys(notif.changes).length > 0 && (
                          <div className="bg-[#0B0C10] border border-[#2A2F3A] rounded-lg p-3 mb-2">
                            <p className="text-[#A1A1AA]/60 text-[10px] uppercase tracking-wider mb-2">Changes</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {Object.entries(notif.changes).map(([key, val]) => {
                                const isOldNew = val && typeof val === 'object' && 'old' in val && 'new' in val;
                                return (
                                  <div key={key} className="flex items-start gap-2 text-xs">
                                    <span className="text-[#A1A1AA]/50 min-w-[80px] capitalize">{key.replace(/_/g, ' ')}:</span>
                                    {isOldNew ? (
                                      <span>
                                        <span className="text-red-400/70 line-through mr-1">{String(val.old ?? '-')}</span>
                                        <ArrowRight className="inline h-3 w-3 text-[#A1A1AA]/40 mx-0.5" />
                                        <span className="text-green-400 font-medium ml-1">{String(val.new ?? '-')}</span>
                                      </span>
                                    ) : (
                                      <span className="text-green-400 font-medium">{typeof val === 'number' ? `$${val.toLocaleString()}` : String(val)}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Footer: time + actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-[#A1A1AA]/50">
                            <Clock className="h-3 w-3" />
                            <span>{formatTimeAgo(notif.created_at)}</span>
                            <span>&middot;</span>
                            <span>{notif.actor_name}</span>
                          </div>
                          
                          {notif.requires_action && !notif.action_taken && (
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 h-7 text-xs px-3"
                                onClick={(e) => { e.stopPropagation(); handleApprove(notif.id); }}
                                data-testid={`approve-${notif.id}`}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                className="bg-red-500/20 text-red-400 hover:bg-red-500/30 h-7 text-xs px-3"
                                onClick={(e) => { e.stopPropagation(); handleReject(notif.id); }}
                                data-testid={`reject-${notif.id}`}
                              >
                                <XCircle className="h-3 w-3 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ======== LEADS TAB ======== */}
        {activeTab === 'leads' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white">Lead Submissions</h2>
              <ExportButtons tab="leads" />
            </div>
            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden">
              {leads.length === 0 ? (
                <div className="p-8 text-center text-[#A1A1AA]">No leads yet.</div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <Table style={{ minWidth: '900px' }}>
                    <TableHeader>
                      <TableRow className="border-[#2A2F3A]">
                        <TableHead className="text-[#A1A1AA]">Name</TableHead>
                        <TableHead className="text-[#A1A1AA]">Email</TableHead>
                        <TableHead className="text-[#A1A1AA]">WhatsApp</TableHead>
                        <TableHead className="text-[#A1A1AA]">Property</TableHead>
                        <TableHead className="text-[#A1A1AA]">Destination</TableHead>
                        <TableHead className="text-[#A1A1AA]">Budget</TableHead>
                        <TableHead className="text-[#A1A1AA]">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead, i) => (
                        <TableRow key={lead.id || i} className="border-[#2A2F3A]">
                          <TableCell className="text-white">{lead.name}</TableCell>
                          <TableCell className="text-[#A1A1AA]">{lead.work_email}</TableCell>
                          <TableCell className="text-[#A1A1AA]">{lead.whatsapp}</TableCell>
                          <TableCell className="text-[#D4AF37] max-w-[180px] truncate text-sm" title={lead.property_title}>{lead.property_title || '-'}</TableCell>
                          <TableCell><Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">{lead.preferred_destination}</Badge></TableCell>
                          <TableCell className="text-[#A1A1AA]">{lead.investment_budget}</TableCell>
                          <TableCell className="text-[#A1A1AA] text-sm">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ======== WHATSAPP TAB ======== */}
        {activeTab === 'whatsapp' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white">WhatsApp Leads</h2>
              <ExportButtons tab="whatsapp" />
            </div>
            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden">
              {whatsappLeads.length === 0 ? (
                <div className="p-8 text-center text-[#A1A1AA]">No WhatsApp leads yet.</div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <Table style={{ minWidth: '700px' }}>
                    <TableHeader>
                      <TableRow className="border-[#2A2F3A]">
                        <TableHead className="text-[#A1A1AA]">Name</TableHead>
                        <TableHead className="text-[#A1A1AA]">Email</TableHead>
                        <TableHead className="text-[#A1A1AA]">Requirement</TableHead>
                        <TableHead className="text-[#A1A1AA]">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {whatsappLeads.map((lead, i) => (
                        <TableRow key={lead.id || i} className="border-[#2A2F3A]">
                          <TableCell className="text-white">{lead.name}</TableCell>
                          <TableCell className="text-[#A1A1AA]">{lead.work_email}</TableCell>
                          <TableCell className="text-[#A1A1AA] max-w-xs truncate">{lead.requirement}</TableCell>
                          <TableCell className="text-[#A1A1AA] text-sm">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ======== NEWSLETTER TAB ======== */}
        {activeTab === 'newsletter' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white">Newsletter Subscribers</h2>
              <ExportButtons tab="newsletter" />
            </div>
            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden">
              {subscribers.length === 0 ? (
                <div className="p-8 text-center text-[#A1A1AA]">No subscribers yet.</div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <Table style={{ minWidth: '500px' }}>
                    <TableHeader>
                      <TableRow className="border-[#2A2F3A]">
                        <TableHead className="text-[#A1A1AA]">Email</TableHead>
                        <TableHead className="text-[#A1A1AA]">Subscribed At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscribers.map((sub, i) => (
                        <TableRow key={sub.id || i} className="border-[#2A2F3A]">
                          <TableCell className="text-white">{sub.email}</TableCell>
                          <TableCell className="text-[#A1A1AA]">{new Date(sub.subscribed_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ======== CAREERS TAB ======== */}
        {activeTab === 'careers' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white">Career Applications</h2>
              <ExportButtons tab="careers" />
            </div>
            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden">
              {careerApplications.length === 0 ? (
                <div className="p-8 text-center text-[#A1A1AA]">No applications yet.</div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <Table style={{ minWidth: '750px' }}>
                    <TableHeader>
                      <TableRow className="border-[#2A2F3A]">
                        <TableHead className="text-[#A1A1AA]">Name</TableHead>
                        <TableHead className="text-[#A1A1AA]">Email</TableHead>
                        <TableHead className="text-[#A1A1AA]">Position</TableHead>
                        <TableHead className="text-[#A1A1AA]">Experience</TableHead>
                        <TableHead className="text-[#A1A1AA]">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {careerApplications.map((app, i) => (
                        <TableRow key={app.id || i} className="border-[#2A2F3A]">
                          <TableCell className="text-white">{app.name}</TableCell>
                          <TableCell className="text-[#A1A1AA]">{app.email}</TableCell>
                          <TableCell><Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">{app.job_title}</Badge></TableCell>
                          <TableCell className="text-[#A1A1AA]">{app.experience}</TableCell>
                          <TableCell className="text-[#A1A1AA] text-sm">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ======== PROPERTIES TAB ======== */}
        {activeTab === 'properties' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-xl text-white">Property Management ({filteredProperties.length} of {properties.length})</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <ExportButtons tab="properties" />
                <Button size="sm" variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white h-8 text-xs" onClick={handleDownloadSampleCsv} data-testid="download-sample-csv">
                  <Download className="h-3 w-3 mr-1" /> Sample CSV
                </Button>
                <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} data-testid="csv-file-input" />
                <Button size="sm" className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 h-8 text-xs" onClick={() => csvInputRef.current?.click()} disabled={csvUploading} data-testid="upload-csv-btn">
                  <Upload className="h-3 w-3 mr-1" /> {csvUploading ? 'Uploading...' : 'Upload CSV'}
                </Button>
                <input ref={brochureInputRef} type="file" accept=".pdf" className="hidden" onChange={handleBrochureUpload} data-testid="brochure-file-input" />
                <Button size="sm" className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 h-8 text-xs" onClick={() => brochureInputRef.current?.click()} disabled={brochureUploading} data-testid="upload-brochure-btn">
                  <FileSpreadsheet className="h-3 w-3 mr-1" /> {brochureUploading ? 'Extracting...' : 'AI from PDF'}
                </Button>
                <Button size="sm" className="btn-luxury h-8 text-xs" data-testid="add-property-btn" onClick={() => { setEditingProperty(null); setIsPropertyModalOpen(true); }}>
                  <Plus className="h-3 w-3 mr-1" /> Add Property
                </Button>
                <Button size="sm" className="h-8 text-xs bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30" data-testid="add-from-url-btn" onClick={() => setShowUrlImport(true)}>
                  <Link2 className="h-3 w-3 mr-1" /> Add from URL
                </Button>
                {/* Custom modal - no Radix Dialog, zero auto-close behavior */}
                {isPropertyModalOpen && (
                  <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="property-modal">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/80" />
                    {/* Content */}
                    <div className="fixed inset-0 overflow-y-auto">
                      <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative w-full max-w-3xl bg-[#1A1D24] border border-[#2A2F3A] text-white rounded-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto">
                          <div className="flex flex-col space-y-1.5 text-left mb-4">
                            <h2 className="text-lg font-semibold leading-none tracking-tight">{editingProperty ? 'Edit Property' : 'Add New Listing'}</h2>
                          </div>
                          <button type="button" onClick={() => { setIsPropertyModalOpen(false); resetPropertyForm(); }} className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity z-10" data-testid="close-property-dialog">
                            <X className="h-4 w-4 text-white" />
                          </button>
                          <PropertyFormWizard
                            editData={editingProperty}
                            submitting={false}
                            tierAllowsAI={true}
                            onSubmit={async (payload) => {
                              try {
                                if (editingProperty) {
                                  await axios.put(`${API_BASE_URL}/api/admin/properties/${editingProperty.id}`, payload, { headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` } });
                                  toast.success('Property updated!');
                                } else {
                                  await axios.post(`${API_BASE_URL}/api/admin/properties`, payload, { headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` } });
                                  toast.success('Property created!');
                                }
                                setIsPropertyModalOpen(false);
                                resetPropertyForm();
                                const token = localStorage.getItem('adminToken');
                                const res = await axios.get(`${API_BASE_URL}/api/admin/properties`, { headers: { Authorization: `Bearer ${token}` } });
                                setProperties((res.data.properties || []));
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to save property');
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {imageUploadProperty && (
                  <ImageUploadBox
                    property={imageUploadProperty}
                    onClose={() => setImageUploadProperty(null)}
                    onImagesUpdated={async (propertyId, allImages) => {
                      try {
                        await axios.put(`${API_BASE_URL}/api/admin/properties/${propertyId}`, {
                          images: allImages,
                          image: allImages[0] || '',
                        }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` } });
                        const token = localStorage.getItem('adminToken');
                        const res = await axios.get(`${API_BASE_URL}/api/admin/properties`, { headers: { Authorization: `Bearer ${token}` } });
                        setProperties(res.data.properties || []);
                      } catch (err) {
                        toast.error('Failed to update images');
                      }
                    }}
                  />
                )}
                {/* URL Import Modal */}
                {showUrlImport && (
                  <div className="fixed inset-0 z-50" data-testid="url-import-modal">
                    <div className="fixed inset-0 bg-black/80" onClick={() => !urlImporting && setShowUrlImport(false)} />
                    <div className="fixed inset-0 overflow-y-auto">
                      <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative w-full max-w-lg bg-[#1A1D24] border border-[#2A2F3A] text-white rounded-xl p-6 shadow-2xl">
                          <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <Link2 className="h-5 w-5 text-purple-400" />
                              </div>
                              <div>
                                <h2 className="text-lg font-semibold">Add Property from URL</h2>
                                <p className="text-[#A1A1AA] text-xs">We&apos;ll scrape the page and create a draft listing</p>
                              </div>
                            </div>
                            {!urlImporting && (
                              <button type="button" onClick={() => setShowUrlImport(false)} className="text-[#A1A1AA] hover:text-white">
                                <X className="h-5 w-5" />
                              </button>
                            )}
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="text-white text-sm block mb-2">Project Name *</label>
                              <input
                                type="text"
                                value={urlImportData.name}
                                onChange={(e) => setUrlImportData(p => ({ ...p, name: e.target.value }))}
                                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#2A2F3A] rounded-lg text-white text-sm focus:outline-none focus:border-[#D4AF37]"
                                placeholder="e.g., Sahyadri Valley Villas"
                                disabled={urlImporting}
                                data-testid="url-import-name"
                              />
                            </div>
                            <div>
                              <label className="text-white text-sm block mb-2">Website URL *</label>
                              <input
                                type="url"
                                value={urlImportData.url}
                                onChange={(e) => setUrlImportData(p => ({ ...p, url: e.target.value }))}
                                className="w-full px-3 py-2 bg-[#0B0C10] border border-[#2A2F3A] rounded-lg text-white text-sm focus:outline-none focus:border-[#D4AF37]"
                                placeholder="https://developer-website.com/project-page"
                                disabled={urlImporting}
                                data-testid="url-import-url"
                              />
                            </div>

                            {urlImporting && (
                              <div className="bg-[#0B0C10] border border-[#2A2F3A] rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-3">
                                  <Loader2 className="h-5 w-5 text-[#D4AF37] animate-spin" />
                                  <span className="text-white text-sm font-medium">Processing URL...</span>
                                </div>
                                <div className="space-y-1.5 text-[#A1A1AA] text-xs">
                                  <p>1. Scraping website content and images...</p>
                                  <p>2. AI extracting property details...</p>
                                  <p>3. Downloading and storing images...</p>
                                </div>
                                <p className="text-[#A1A1AA] text-xs mt-3 italic">This may take 30-60 seconds</p>
                              </div>
                            )}
                          </div>

                          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#2A2F3A]">
                            {!urlImporting && (
                              <Button variant="outline" onClick={() => setShowUrlImport(false)} className="border-[#2A2F3A] text-[#A1A1AA]">
                                Cancel
                              </Button>
                            )}
                            <Button
                              className="btn-luxury"
                              disabled={!urlImportData.name.trim() || !urlImportData.url.trim() || urlImporting}
                              data-testid="url-import-submit"
                              onClick={async () => {
                                setUrlImporting(true);
                                try {
                                  const res = await axios.post(`${API_BASE_URL}/api/admin/properties/from-url`, urlImportData, {
                                    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                                    timeout: 120000
                                  });
                                  const stats = res.data.scrape_stats || {};
                                  toast.success(`Created draft: "${urlImportData.name}" — ${stats.images_stored || 0} images stored`);
                                  setShowUrlImport(false);
                                  setUrlImportData({ name: '', url: '' });
                                  const token = localStorage.getItem('adminToken');
                                  const propsRes = await axios.get(`${API_BASE_URL}/api/admin/properties`, { headers: { Authorization: `Bearer ${token}` } });
                                  setProperties(propsRes.data.properties || []);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to import from URL');
                                } finally {
                                  setUrlImporting(false);
                                }
                              }}
                            >
                              {urlImporting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing...</> : <><Link2 className="h-4 w-4 mr-1" /> Import from URL</>}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Filters Bar */}
            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-4 mb-4" data-testid="property-filters">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-white text-sm font-medium">Filters</span>
                {Object.values(propertyFilters).some(v => v !== 'all') && (
                  <button onClick={() => setPropertyFilters({ country: 'all', type: 'all', budget: 'all', config: 'all', partner: 'all' })} className="text-xs text-[#D4AF37] hover:underline ml-auto" data-testid="clear-filters-btn">Clear All</button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Select value={propertyFilters.country} onValueChange={(v) => setPropertyFilters({...propertyFilters, country: v})}>
                  <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white text-sm h-9" data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                    <SelectItem value="all" className="text-white">All Countries</SelectItem>
                    {filterOptions.allCountries.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={propertyFilters.budget} onValueChange={(v) => setPropertyFilters({...propertyFilters, budget: v})}>
                  <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white text-sm h-9" data-testid="filter-budget"><SelectValue placeholder="Budget" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                    <SelectItem value="all" className="text-white">All Budgets</SelectItem>
                    <SelectItem value="under500k" className="text-white">Under $500K</SelectItem>
                    <SelectItem value="500k-1m" className="text-white">$500K - $1M</SelectItem>
                    <SelectItem value="1m-5m" className="text-white">$1M - $5M</SelectItem>
                    <SelectItem value="5m-10m" className="text-white">$5M - $10M</SelectItem>
                    <SelectItem value="above10m" className="text-white">Above $10M</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={propertyFilters.type} onValueChange={(v) => setPropertyFilters({...propertyFilters, type: v})}>
                  <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white text-sm h-9" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                    <SelectItem value="all" className="text-white">All Types</SelectItem>
                    {filterOptions.allTypes.map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={propertyFilters.config} onValueChange={(v) => setPropertyFilters({...propertyFilters, config: v})}>
                  <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white text-sm h-9" data-testid="filter-config"><SelectValue placeholder="Config" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                    <SelectItem value="all" className="text-white">All Configs</SelectItem>
                    {filterOptions.allBedrooms.map(b => <SelectItem key={b} value={b.toString()} className="text-white">{b} BHK</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={propertyFilters.partner} onValueChange={(v) => setPropertyFilters({...propertyFilters, partner: v})}>
                  <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white text-sm h-9" data-testid="filter-partner"><SelectValue placeholder="Partner" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                    <SelectItem value="all" className="text-white">All Partners</SelectItem>
                    {filterOptions.allPartners.map(d => <SelectItem key={d} value={d} className="text-white">{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Property Table with AdminTable */}
            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
              <AdminTable
                data={filteredProperties}
                idField="id"
                onBulkDelete={(ids) => handleBulkDelete('properties', ids)}
                emptyMessage="No properties match your filters."
                columns={[
                  { key: 'image', label: 'Img', render: (p) => p.image ? <img src={p.image.startsWith('/api/files/') ? p.image.replace('/api/files/', '/api/files/thumb/100/') : p.image} alt="" className="w-14 h-10 object-cover rounded" loading="lazy" /> : <div className="w-14 h-10 bg-[#2A2F3A] rounded flex items-center justify-center text-[#A1A1AA] text-xs">No img</div> },
                  { key: 'title', label: 'Name', className: 'text-white font-medium max-w-[200px] truncate', render: (p) => <span title={p.title}>{p.title}</span> },
                  { key: 'country', label: 'Location', render: (p) => <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">{p.micromarket ? `${p.micromarket}, ${p.city}` : `${p.city}, ${p.country}`}</Badge> },
                  { key: 'property_type', label: 'Type', className: 'text-[#A1A1AA]' },
                  { key: 'price', label: 'Price', className: 'text-[#D4AF37] font-medium', render: (p) => p.price_inr ? `₹${(p.price_inr/10000000).toFixed(1)} Cr` : `$${(p.price_usd || 0).toLocaleString()}` },
                  { key: 'visible', label: 'Visible', className: 'text-center', render: (p) => <Switch checked={p.visible !== false} onCheckedChange={() => handleToggleVisibility(p.id)} data-testid={`toggle-${p.id}`} /> },
                  { key: 'actions', label: 'Actions', render: (p) => (
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setImageUploadProperty(p)} className="text-[#D4AF37] hover:text-[#D4AF37]/80 h-8 w-8 p-0" title="Upload Images" data-testid={`upload-images-${p.id}`}><Camera className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEditProperty(p)} className="text-[#A1A1AA] hover:text-white h-8 w-8 p-0" data-testid={`edit-${p.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteProperty(p.id)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0" data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )},
                ]}
              />
            </div>
          </motion.div>
        )}

        {/* ======== PARTNERS TAB ======== */}
        {activeTab === 'partners' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-white" data-testid="partners-heading">Partners ({partners.length})</h2>
              <div className="flex items-center gap-2">
                <ExportButtons tab="partners" />
                <Dialog open={isPartnerModalOpen} onOpenChange={(open) => { setIsPartnerModalOpen(open); if (!open) resetPartnerForm(); }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="btn-luxury h-8 text-xs" data-testid="add-partner-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add Partner
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1A1D24] border-[#2A2F3A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handlePartnerSubmit} className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label className="text-white mb-2 block">Partner Name *</Label>
                          <Input value={partnerForm.name} onChange={(e) => setPartnerForm({...partnerForm, name: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" required data-testid="partner-name-input" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Email *</Label>
                          <Input type="email" value={partnerForm.email} onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})} placeholder="partner@company.com" className="bg-[#0B0C10] border-[#2A2F3A] text-white" required={!editingPartner} data-testid="partner-email-input" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">{editingPartner ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
                          <Input type="password" value={partnerForm.password} onChange={(e) => setPartnerForm({...partnerForm, password: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" required={!editingPartner} data-testid="partner-password-input" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Company Name</Label>
                          <Input value={partnerForm.company} onChange={(e) => setPartnerForm({...partnerForm, company: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" data-testid="partner-company-input" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Country</Label>
                          <Input value={partnerForm.country} onChange={(e) => setPartnerForm({...partnerForm, country: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" list="partner-country-suggestions" />
                          <datalist id="partner-country-suggestions">
                            {dynamicCountries.map(c => <option key={c} value={c} />)}
                          </datalist>
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Status</Label>
                          <Select value={partnerForm.status} onValueChange={(v) => setPartnerForm({...partnerForm, status: v})}>
                            <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                              <SelectItem value="Active" className="text-white">Active</SelectItem>
                              <SelectItem value="Inactive" className="text-white">Inactive</SelectItem>
                              <SelectItem value="Pending" className="text-white">Pending</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Tier</Label>
                          <Select value={partnerForm.tier} onValueChange={(v) => setPartnerForm({...partnerForm, tier: v})}>
                            <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white" data-testid="partner-tier-select"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                              <SelectItem value="Basic" className="text-white">Basic</SelectItem>
                              <SelectItem value="Professional" className="text-white">Professional</SelectItem>
                              <SelectItem value="Enterprise" className="text-white">Enterprise</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Credits</Label>
                          <Input type="number" value={partnerForm.credits} onChange={(e) => setPartnerForm({...partnerForm, credits: parseInt(e.target.value) || 0})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" data-testid="partner-credits-input" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Partner Type</Label>
                          <Select value={partnerForm.partner_type} onValueChange={(v) => setPartnerForm({...partnerForm, partner_type: v})}>
                            <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                              <SelectItem value="developer" className="text-white">Developer</SelectItem>
                              <SelectItem value="agent" className="text-white">Agent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Billing Cycle</Label>
                          <Select value={partnerForm.billing_cycle} onValueChange={(v) => setPartnerForm({...partnerForm, billing_cycle: v})}>
                            <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                              <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                              <SelectItem value="annual" className="text-white">Annual</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Phone</Label>
                          <Input value={partnerForm.phone} onChange={(e) => setPartnerForm({...partnerForm, phone: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">City</Label>
                          <Input value={partnerForm.city} onChange={(e) => setPartnerForm({...partnerForm, city: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Logo URL</Label>
                          <Input value={partnerForm.logo_url} onChange={(e) => setPartnerForm({...partnerForm, logo_url: e.target.value})} placeholder="https://..." className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Website</Label>
                          <Input value={partnerForm.website} onChange={(e) => setPartnerForm({...partnerForm, website: e.target.value})} placeholder="https://..." className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-white mb-2 block">Description</Label>
                          <Textarea value={partnerForm.description} onChange={(e) => setPartnerForm({...partnerForm, description: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white min-h-[80px]" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full btn-luxury" data-testid="save-partner-btn">
                        <Save className="h-4 w-4 mr-2" /> {editingPartner ? 'Update' : 'Create'} Partner
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
              <AdminTable
                data={partners}
                idField="id"
                onBulkDelete={(ids) => handleBulkDelete('partners', ids)}
                emptyMessage="No partners registered yet."
                columns={[
                  { key: 'logo', label: '', render: (p) => p.logo_url ? <img src={p.logo_url} alt="" className="w-10 h-10 object-contain rounded bg-white/5" /> : <div className="w-10 h-10 rounded bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] font-bold text-sm">{(p.name||'?').charAt(0)}</div> },
                  { key: 'name', label: 'Name', className: 'text-white font-medium' },
                  { key: 'email', label: 'Email', className: 'text-[#A1A1AA] text-sm' },
                  { key: 'tier', label: 'Tier', render: (p) => <Badge className={`border-0 text-[10px] ${p.tier === 'Enterprise' ? 'bg-violet-400/20 text-violet-300' : p.tier === 'Professional' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#A1A1AA]/20 text-[#A1A1AA]'}`}>{p.tier || 'Basic'}</Badge> },
                  { key: 'status', label: 'Status', render: (p) => <Badge className={`border-0 ${p.status === 'Active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{p.status}</Badge> },
                  { key: 'actions', label: 'Actions', render: (p) => (
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEditPartner(p)} className="text-[#A1A1AA] hover:text-white h-8 w-8 p-0" data-testid={`edit-partner-${p.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeletePartner(p.id)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0" data-testid={`delete-partner-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )},
                ]}
              />
            </div>
          </motion.div>
        )}

        {/* ======== COUNTRIES TAB ======== */}
        {activeTab === 'countries' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-[#A1A1AA] text-sm">Manage countries, visa programs, and residency information</p>
              <Dialog open={isCountryModalOpen} onOpenChange={(open) => { setIsCountryModalOpen(open); if (!open) setCountryForm({ name: '', region: '', visa_name: '', visa_type: '', min_investment: '', visa_benefits: '', visa_requirements: '', processing_time: '', validity: '', residency_writeup: '', image: '' }); }}>
                <DialogTrigger asChild>
                  <Button className="btn-luxury" data-testid="add-country-btn"><Plus className="h-4 w-4 mr-2" /> Add Country</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A1D24] border-[#2A2F3A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Country</DialogTitle>
                    <p className="text-[#A1A1AA] text-sm mt-1">Provide visa & residency details. AI will enrich with destination content automatically.</p>
                  </DialogHeader>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setCountryCreating(true);
                    try {
                      const payload = {
                        name: countryForm.name,
                        region: countryForm.region,
                        visa_name: countryForm.visa_name || '',
                        visa_type: countryForm.visa_type || '',
                        min_investment: parseFloat(countryForm.min_investment) || 0,
                        visa_benefits: countryForm.visa_benefits ? countryForm.visa_benefits.split('\n').filter(Boolean) : [],
                        visa_requirements: countryForm.visa_requirements ? countryForm.visa_requirements.split('\n').filter(Boolean) : [],
                        processing_time: countryForm.processing_time || '',
                        validity: countryForm.validity || '',
                        residency_writeup: countryForm.residency_writeup || '',
                        image: countryForm.image || ''
                      };
                      await createCountry(payload);
                      toast.success(`Country "${countryForm.name}" added! AI is generating destination content.`);
                      setIsCountryModalOpen(false);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to create country');
                    } finally {
                      setCountryCreating(false);
                    }
                  }} className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">Country Name *</Label>
                        <Input value={countryForm.name} onChange={(e) => setCountryForm({...countryForm, name: e.target.value})} placeholder="e.g. Thailand" className="bg-[#0B0C10] border-[#2A2F3A] text-white" required data-testid="country-name-input" />
                      </div>
                      <div>
                        <Label className="text-white mb-2 block">Region *</Label>
                        <Select value={countryForm.region} onValueChange={(v) => setCountryForm({...countryForm, region: v})}>
                          <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder="Select region" /></SelectTrigger>
                          <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{regions.map(r => <SelectItem key={r} value={r} className="text-white">{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="border border-[#2A2F3A] rounded-lg p-4">
                      <h3 className="text-white font-medium mb-3">Visa & Residency Information</h3>
                      <p className="text-[#A1A1AA] text-xs mb-4">Fill in what you know. Leave blank fields to be auto-populated by AI.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white mb-2 block">Visa Program Name</Label>
                          <Input value={countryForm.visa_name} onChange={(e) => setCountryForm({...countryForm, visa_name: e.target.value})} placeholder="e.g. Thailand Elite Visa" className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Visa Type</Label>
                          <Input value={countryForm.visa_type} onChange={(e) => setCountryForm({...countryForm, visa_type: e.target.value})} placeholder="e.g. Investor Visa" className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Min Investment (USD)</Label>
                          <Input type="number" value={countryForm.min_investment} onChange={(e) => setCountryForm({...countryForm, min_investment: e.target.value})} placeholder="250000" className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Processing Time</Label>
                          <Input value={countryForm.processing_time} onChange={(e) => setCountryForm({...countryForm, processing_time: e.target.value})} placeholder="e.g. 3-6 months" className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Validity</Label>
                          <Input value={countryForm.validity} onChange={(e) => setCountryForm({...countryForm, validity: e.target.value})} placeholder="e.g. 5 years (renewable)" className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div>
                          <Label className="text-white mb-2 block">Cover Image URL</Label>
                          <Input value={countryForm.image} onChange={(e) => setCountryForm({...countryForm, image: e.target.value})} placeholder="https://..." className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-white mb-2 block">Key Benefits (one per line)</Label>
                          <Textarea value={countryForm.visa_benefits} onChange={(e) => setCountryForm({...countryForm, visa_benefits: e.target.value})} placeholder={"Tax exemptions on foreign income\nAccess to international banking\nFamily residency included"} className="bg-[#0B0C10] border-[#2A2F3A] text-white min-h-[80px]" />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-white mb-2 block">Requirements (one per line)</Label>
                          <Textarea value={countryForm.visa_requirements} onChange={(e) => setCountryForm({...countryForm, visa_requirements: e.target.value})} placeholder={"Minimum property investment\nClean criminal record\nHealth insurance"} className="bg-[#0B0C10] border-[#2A2F3A] text-white min-h-[80px]" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Residency & Visa Writeup / Notes</Label>
                      <p className="text-[#A1A1AA] text-xs mb-2">Paste detailed visa/residency information, regulatory notes, or any intel that AI should use to create accurate content.</p>
                      <Textarea value={countryForm.residency_writeup} onChange={(e) => setCountryForm({...countryForm, residency_writeup: e.target.value})} placeholder="Paste detailed visa norms, residency requirements, or any relevant information here..." className="bg-[#0B0C10] border-[#2A2F3A] text-white min-h-[120px]" />
                    </div>
                    <Button type="submit" className="w-full btn-luxury" disabled={countryCreating} data-testid="create-country-btn">
                      {countryCreating ? (
                        <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" /> Creating & Generating AI Content...</>
                      ) : (
                        <><Save className="h-4 w-4 mr-2" /> Create Country</>
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl">
              {adminCountries.length === 0 ? (
                <div className="p-8 text-center text-[#A1A1AA]">No countries configured yet.</div>
              ) : (
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <Table style={{ minWidth: '800px' }}>
                      <TableHeader>
                        <TableRow className="border-[#2A2F3A]">
                          <TableHead className="text-[#A1A1AA]">Country</TableHead>
                          <TableHead className="text-[#A1A1AA]">Region</TableHead>
                          <TableHead className="text-[#A1A1AA]">Visa Program</TableHead>
                          <TableHead className="text-[#A1A1AA]">Min Investment</TableHead>
                          <TableHead className="text-[#A1A1AA]">Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {adminCountries.map((c, i) => (
                          <TableRow key={i} className="border-[#2A2F3A] hover:bg-[#2A2F3A]/30">
                            <TableCell className="text-white font-medium">{c.name}</TableCell>
                            <TableCell><Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">{c.region || '-'}</Badge></TableCell>
                            <TableCell className="text-[#A1A1AA]">{c.visa_info?.name || c.visa_info?.type || '-'}</TableCell>
                            <TableCell className="text-white">{c.visa_info?.min_investment ? `$${(c.visa_info.min_investment / 1000).toFixed(0)}K` : '-'}</TableCell>
                            <TableCell>
                              <Badge className={`border-0 ${c.source === 'admin' ? 'bg-green-500/10 text-green-400' : c.source === 'auto-generated' ? 'bg-blue-500/10 text-blue-400' : 'bg-[#2A2F3A] text-[#A1A1AA]'}`}>
                                {c.source || 'built-in'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
